import React from 'react';

import { DISPLAY_DATE_FORMAT } from '@cd3p/core/utils/common';
import { timeField } from '@cd3p/core/utils/fields';
import { DateField, FormField, TimePicker } from 'components';
import { FieldError } from 'react-hook-form';

import { dayjs, useFormContext, useTranslation } from 'third-party';

interface Props {
  acceptPastDate?: boolean;
  isTimeRequired?: boolean;
  isDateRequired?: boolean;
  isDisabled?: boolean;
  showError?: boolean;
  timeFieldName: string;
  dateFieldName: string;
}

const isAfterNow = (dateValue: any, timeValue: any) =>
  !dayjs().isAfter(
    dayjs(
      `${dayjs(dateValue).format(DISPLAY_DATE_FORMAT)}T${timeValue}`,
      `${DISPLAY_DATE_FORMAT}Thh:mm A`,
    ),
  );

export const DateTimeForm = React.memo(
  ({
    acceptPastDate = true,
    showError = false,
    timeFieldName,
    dateFieldName,
    isDisabled = false,
    isTimeRequired,
    isDateRequired,
  }: Props) => {
    const { t } = useTranslation();

    const {
      watch,
      control,
      formState: { errors },
    } = useFormContext();

    const watchedDateValue = watch(dateFieldName);
    const watchedTimeValue = watch(timeFieldName);

    return (
      <>
        <DateField
          fieldName={dateFieldName}
          fieldError={errors[dateFieldName] as FieldError}
          isRequired={isDateRequired}
          label={t('order.sectionField.deliveryDate')}
          requiredErrorMessage={t('common.form.error.emptyField')}
          control={control}
          isDisabled={isDisabled}
          showError={showError}
          rules={{
            validate: {
              incorrectDate: (value: any) =>
                dayjs(value).isValid() || t('common.form.date.error.invalid'),
              isPastDate: (value: any) => {
                return (
                  acceptPastDate ||
                  dayjs(value).isToday() ||
                  isAfterNow(value, watchedTimeValue) ||
                  t('common.form.date.error.pastDate')
                );
              },
            },
            deps: [timeFieldName],
          }}
        />
        <FormField
          fieldName={timeFieldName}
          fieldError={errors[timeFieldName] as FieldError}
          isRequired={isTimeRequired}
          label={t('order.sectionField.deliveryTime')}
          requiredErrorMessage={t('common.form.error.emptyField')}
          control={control}
          showError={showError}
          isDisabled={isDisabled}
          rules={{
            validate: {
              incorrectTime: (value: string) =>
                timeField.validate(value) ||
                t('common.form.time.error.invalid'),
              isPastTime: (value: any) =>
                acceptPastDate ||
                isAfterNow(watchedDateValue, value) ||
                t('common.form.time.error.pastTime'),
            },
            deps: [dateFieldName],
          }}
          render={({ field }) => (
            <TimePicker
              value={field.value}
              onChange={field.onChange}
              disabled={isDisabled}
              error={showError && !!errors[timeFieldName]}
            />
          )}
        />
      </>
    );
  },
);

DateTimeForm.displayName = 'DateTimeForm';
